import { Decision, DecisionWithId } from '@livv/models';
import { QuerySnapshot, documentId, getDocs, query, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { decisionsCollection } from '@utils/collections';

const IDS_PER_ROUND = 10;

type UseSameCaseDecisions = (
    decisionIds: string[],
    currentId: string,
) => [decisions: DecisionWithId[], isLoading: boolean, error: string];
type DecisionsSnapshotPromise = Promise<QuerySnapshot<Decision>>;

/**
 * Fetch decisions sited in 'sameCase' field.
 * Documents must be fetched by batch of 10 or less.
 * https://stackoverflow.com/questions/61354866/is-there-a-workaround-for-the-firebase-query-in-limit-to-10
 */
const useSameCaseDecisions: UseSameCaseDecisions = (decisionIds, currentId) => {
    const [decisions, setDecisions] = useState<DecisionWithId[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchDecisions = async (ids: string[]) => {
            setIsLoading(true);
            try {
                const rounds = Math.ceil(ids.length / IDS_PER_ROUND);
                const snapshotsPromises: DecisionsSnapshotPromise[] = [];
                for (let i = 0; i < rounds; i++) {
                    const idsToFetch = ids.slice(i * IDS_PER_ROUND, (i + 1) * IDS_PER_ROUND);
                    if (idsToFetch) {
                        snapshotsPromises.push(
                            getDocs(
                                query(decisionsCollection, where(documentId(), 'in', idsToFetch)),
                            ) as unknown as DecisionsSnapshotPromise,
                        );
                    }
                }
                const decisionsSnapshots = await Promise.all(snapshotsPromises);
                setDecisions(
                    decisionsSnapshots.flatMap((snap) =>
                        snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
                    ),
                );
            } catch (e) {
                setError(JSON.stringify(e));
            } finally {
                setIsLoading(false);
            }
        };
        fetchDecisions(decisionIds);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentId]);

    return [decisions, isLoading, error];
};

export default useSameCaseDecisions;
